var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { v4 as uuidv4 } from 'uuid';
import * as uuidv5 from 'uuid/v5';
import { LocalStorageKeys } from '@/types';
export var AuthSessionChangedEvent = 'auth-session-changed';
var notifyAuthSessionChanged = function () {
    if (typeof window !== 'undefined')
        window.dispatchEvent(new Event(AuthSessionChangedEvent));
};
var AuthLoginCancelledError = /** @class */ (function (_super) {
    __extends(AuthLoginCancelledError, _super);
    function AuthLoginCancelledError() {
        var _this = _super.call(this, 'ログイン試行が変更されました') || this;
        Object.setPrototypeOf(_this, AuthLoginCancelledError.prototype);
        return _this;
    }
    return AuthLoginCancelledError;
}(Error));
export { AuthLoginCancelledError };
var authSessionKeys = [
    LocalStorageKeys.JWT,
    LocalStorageKeys.AuthSessionID,
    LocalStorageKeys.AuthLoginAttemptID,
    LocalStorageKeys.RefreshToken,
    LocalStorageKeys.Departments,
    LocalStorageKeys.UserType,
    LocalStorageKeys.CompanyId,
    LocalStorageKeys.CompanyCID
];
var getStorageForUserType = function (userType) {
    return userType === 'BranuUser' ? sessionStorage : localStorage;
};
var hasBranuSession = function () {
    return !!sessionStorage.getItem(LocalStorageKeys.JWT);
};
var getActiveStorage = function () {
    return hasBranuSession() ? sessionStorage : localStorage;
};
export var getAuthItem = function (key, userType) {
    return (userType ? getStorageForUserType(userType) : getActiveStorage()).getItem(key);
};
var saveStorageSessionId = function (storage, id, jwt, previousJwt) {
    storage.setItem(LocalStorageKeys.AuthSessionID, JSON.stringify({ id: id, jwt: jwt, previousJwt: previousJwt }));
};
var getStorageSessionId = function (storage) {
    var jwt = storage.getItem(LocalStorageKeys.JWT);
    if (!jwt)
        return null;
    var serialized = storage.getItem(LocalStorageKeys.AuthSessionID);
    if (jwt !== storage.getItem(LocalStorageKeys.JWT) ||
        serialized !== storage.getItem(LocalStorageKeys.AuthSessionID))
        return getStorageSessionId(storage);
    var saved = serialized
        ? JSON.parse(serialized)
        : null;
    if (saved &&
        typeof saved.id === 'string' &&
        saved.id &&
        (saved.jwt === jwt || saved.previousJwt === jwt))
        return saved.id;
    // 旧バージョンの別タブがJWTを交換した場合も、新しいログインとして要求を分離する
    // 識別子のない既存セッションを複数タブが同時に読む場合も、同じ識別子へ揃える
    var sessionId = uuidv5(jwt, uuidv5.URL);
    saveStorageSessionId(storage, sessionId, jwt);
    return sessionId;
};
export var getAuthSessionId = function (userType) {
    return getStorageSessionId(userType ? getStorageForUserType(userType) : getActiveStorage());
};
export var beginAuthLoginAttempt = function (userType) {
    if (userType === 'User')
        localStorage.setItem(LocalStorageKeys.AuthUserLoginID, uuidv4());
    // 新しい試行を開始した時点で旧認証を失効させ、更新応答の所有権を引き継がせない
    removeStoredAuthSession();
    notifyAuthSessionChanged();
    var attempt = {
        id: uuidv4(),
        userType: userType,
        sessionId: getAuthSessionId(userType),
        sharedUserLoginId: localStorage.getItem(LocalStorageKeys.AuthUserLoginID)
    };
    getStorageForUserType(userType).setItem(LocalStorageKeys.AuthLoginAttemptID, attempt.id);
    getStorageForUserType(userType === 'User' ? 'BranuUser' : 'User').removeItem(LocalStorageKeys.AuthLoginAttemptID);
    return attempt;
};
export var isCurrentAuthLoginAttempt = function (attempt) {
    return getStorageForUserType(attempt.userType).getItem(LocalStorageKeys.AuthLoginAttemptID) ===
        attempt.id &&
        getAuthSessionId(attempt.userType) === attempt.sessionId &&
        localStorage.getItem(LocalStorageKeys.AuthUserLoginID) === attempt.sharedUserLoginId;
};
export var ensureCurrentAuthLoginAttempt = function (attempt) {
    if (!isCurrentAuthLoginAttempt(attempt))
        throw new AuthLoginCancelledError();
};
// ユーザー種別を指定しない更新は現在の認証セッションの保存先へ書き戻す
export var setAuthItem = function (key, value, userType) {
    var storage = userType ? getStorageForUserType(userType) : getActiveStorage();
    var sessionId = key === LocalStorageKeys.JWT ? getStorageSessionId(storage) : null;
    // 別タブがJWTの保存途中を読んでも、同じセッションの更新として扱う
    if (sessionId)
        saveStorageSessionId(storage, sessionId, value, storage.getItem(key) || undefined);
    storage.setItem(key, value);
    if (sessionId)
        saveStorageSessionId(storage, sessionId, value);
};
export var removeAuthItem = function (key, userType) {
    if (userType) {
        getStorageForUserType(userType).removeItem(key);
        return;
    }
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
};
var removeStoredAuthSession = function (userType) {
    authSessionKeys.forEach(function (key) { return removeAuthItem(key, userType); });
    // 通常ユーザーのdevice_uuidはlocalStorageで共有するため残す
    if (userType !== 'User')
        removeAuthItem(LocalStorageKeys.DeviceUUID, 'BranuUser');
};
export var clearAuthSession = function (userType) {
    if (userType !== 'BranuUser')
        localStorage.setItem(LocalStorageKeys.AuthUserLoginID, uuidv4());
    removeStoredAuthSession(userType);
    notifyAuthSessionChanged();
};
export var saveAuthSession = function (session, attempt) {
    if (attempt) {
        ensureCurrentAuthLoginAttempt(attempt);
        if (attempt.userType !== session.userType)
            throw new AuthLoginCancelledError();
    }
    // ログイン切替時は両方の認証情報を削除してから対象ユーザーの保存先へ書き込む
    removeStoredAuthSession();
    if (!attempt && session.userType === 'User') {
        localStorage.setItem(LocalStorageKeys.AuthUserLoginID, uuidv4());
    }
    saveStorageSessionId(getStorageForUserType(session.userType), uuidv4(), session.jwt);
    setAuthItem(LocalStorageKeys.JWT, session.jwt, session.userType);
    setAuthItem(LocalStorageKeys.RefreshToken, session.refreshToken, session.userType);
    setAuthItem(LocalStorageKeys.DeviceUUID, session.deviceUUID, session.userType);
    setAuthItem(LocalStorageKeys.Departments, JSON.stringify(session.departments), session.userType);
    // fetchMe前の初期処理でもユーザー種別を判定できるよう同期的に保存する
    setAuthItem(LocalStorageKeys.UserType, session.userType, session.userType);
    if (session.companyId) {
        setAuthItem(LocalStorageKeys.CompanyId, session.companyId, session.userType);
    }
    if (session.companyCID) {
        setAuthItem(LocalStorageKeys.CompanyCID, session.companyCID, session.userType);
    }
    if (attempt) {
        setAuthItem(LocalStorageKeys.AuthLoginAttemptID, attempt.id, attempt.userType);
        attempt.sessionId = getAuthSessionId(attempt.userType);
    }
    // BranuUserのlastUrlは代理ログイン前の会社へ戻さない
    removeAuthItem(LocalStorageKeys.LastURL, 'BranuUser');
    notifyAuthSessionChanged();
};
